import { createMarkPoints, createThresholdMarkAreas } from '../chartPlugins/markPointPlugins.jsx';

/**
 * @fileoverview Chart configuration strategy builders for BaseChart options.
 * Provides different configuration builders based on chart type and requirements.
 * @module chartConfigs/chartConfigStrategies
 */

/**
 * Builds a tooltip formatter function that supports custom callbacks.
 *
 * @param {Object} options - Chart.js options object with plugin configurations
 * @param {Array} series - ECharts series array
 * @param {Object} dataSets - Original Chart.js datasets object
 * @returns {Function} Formatter function for ECharts tooltip
 */
export const buildTooltipFormatter = (options, series, dataSets) => {
  return (params) => {
    // Use custom tooltip if provided
    if (options?.plugins?.tooltip?.callbacks?.label) {
      const lines = [];

      // Add title
      if (options?.plugins?.tooltip?.callbacks?.title) {
        const title = options.plugins.tooltip.callbacks.title(params);
        if (title) {
          lines.push(`<div style="font-weight: bold; margin-bottom: 5px;">${title}</div>`);
        }
      } else if (params.length > 0) {
        lines.push(`<div style="font-weight: bold; margin-bottom: 5px;">${params[0].name}</div>`);
      }

      // Add each series data
      params.forEach((param) => {
        const rawData = series[param.seriesIndex]._rawData?.[param.dataIndex];
        const metaData = series[param.seriesIndex]._meta?.data?.[param.dataIndex];

        const context = {
          label: param.name,
          parsed: { y: param.value },
          dataset: {
            ...dataSets.datasets[param.seriesIndex],
            _meta: series[param.seriesIndex]._meta,
          },
          dataIndex: param.dataIndex,
          raw: metaData || rawData, // Use metaData (has sensordepths, depth1, etc.) instead of rawData (only x,y)
        };

        const labelResult = options.plugins.tooltip.callbacks.label(context);

        // Handle array of label lines
        if (Array.isArray(labelResult)) {
          labelResult.forEach((line) => {
            lines.push(`<div style="margin: 2px 0;">${line}</div>`);
          });
        } else {
          lines.push(`<div style="margin: 2px 0;">${labelResult}</div>`);
        }
      });

      return lines.join('');
    }

    // Default tooltip
    const lines = [
      `<div style="font-weight: bold; margin-bottom: 5px;">${params[0]?.name || ''}</div>`,
    ];
    params.forEach((param) => {
      lines.push(
        `<div style="margin: 2px 0;">${param.marker} ${param.seriesName}: ${param.value}</div>`,
      );
    });
    return lines.join('');
  };
};

/**
 * Builds Y-axis configuration from Chart.js scales definition.
 * Supports both single and dual Y-axes.
 *
 * @param {Object} dataSets - Chart.js datasets object
 * @param {Object} options - Chart.js options with scales configuration
 * @returns {Object} Object containing:
 *   - yAxisConfig: ECharts Y-axis configuration (single object or array)
 *   - yAxisMap: Mapping of yAxisID to index
 *   - hasDualAxis: Boolean indicating if multiple Y-axes exist
 */
export const buildYAxisConfig = (dataSets, options) => {
  // Determine unique Y-axis IDs
  const yAxisIds = new Set();
  dataSets?.datasets?.forEach((d) => {
    if (d.yAxisID) yAxisIds.add(d.yAxisID);
  });
  const hasDualAxis = yAxisIds.size > 1;

  // Map yAxisID to index
  const yAxisMap = {};
  let index = 0;
  yAxisIds.forEach((id) => {
    yAxisMap[id] = index++;
  });

  const yAxisConfig = hasDualAxis
    ? Array.from(yAxisIds).map((id, idx) => ({
        type: 'value',
        name: options?.scales?.[id]?.title?.text || '',
        position: idx === 0 ? 'left' : 'right',
        show: options?.scales?.[id]?.display !== false,
        min: options?.scales?.[id]?.min ?? (options?.scales?.[id]?.beginAtZero ? 0 : undefined),
        max: options?.scales?.[id]?.max,
        axisLabel: {
          color: options?.scales?.[id]?.ticks?.color || '#000',
          formatter: options?.scales?.[id]?.ticks?.callback || undefined,
        },
        splitLine: {
          show: idx === 0,
          lineStyle: {
            color: options?.scales?.[id]?.grid?.color || '#e0e0e0',
          },
        },
      }))
    : {
        type: 'value',
        name: options?.scales?.y?.title?.text || '',
        min: options?.scales?.y?.min ?? (options?.scales?.y?.beginAtZero ? 0 : undefined),
        max: options?.scales?.y?.max,
        axisLabel: {
          color: options?.scales?.y?.ticks?.color || '#000',
          formatter: options?.scales?.y?.ticks?.callback || undefined,
        },
        splitLine: {
          lineStyle: {
            color: options?.scales?.y?.grid?.color || '#e0e0e0',
          },
        },
      };

  return { yAxisConfig, yAxisMap, hasDualAxis };
};

/**
 * Updates series array with correct yAxisIndex based on yAxisID mapping.
 *
 * @param {Array} series - ECharts series array
 * @param {Object} dataSets - Chart.js datasets object
 * @param {Object} yAxisMap - Mapping of yAxisID to index
 * @returns {Array} Updated series array with yAxisIndex set
 */
export const updateSeriesWithYAxis = (series, dataSets, yAxisMap) => {
  return series.map((s, i) => {
    const dataset = dataSets.datasets[i];
    return {
      ...s,
      yAxisIndex: dataset.yAxisID ? yAxisMap[dataset.yAxisID] || 0 : 0,
    };
  });
};

/**
 * Builds standard chart configuration for basic line/bar charts.
 * Used when no special plugins are detected.
 *
 * @param {Object} params - Configuration parameters
 * @param {Array} params.xAxisData - X-axis data labels
 * @param {Array} params.series - ECharts series array
 * @param {Object} params.dataSets - Chart.js datasets object
 * @param {Object} params.options - Chart.js options object
 * @param {string} params.type - Chart type ('line', 'bar', etc.)
 * @returns {Object} Complete ECharts configuration object
 */
export const buildStandardChartConfig = ({ xAxisData, series, dataSets, options, type }) => {
  const actualXLabels = options?.scales?.x?.labels || xAxisData;
  const { yAxisConfig, yAxisMap, hasDualAxis } = buildYAxisConfig(dataSets, options);

  const updatedSeries = updateSeriesWithYAxis(series, dataSets, yAxisMap);

  return {
    animation: options?.animation !== false,
    title: {
      text: options?.plugins?.title?.text || options?.title?.text || '',
      left: 'center',
      top: 12,
      textStyle: {
        color: options?.plugins?.title?.color || options?.title?.color || '#000',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      confine: true,
      backgroundColor: options?.plugins?.tooltip?.backgroundColor || 'rgba(255, 255, 255, 0.9)',
      borderColor: options?.plugins?.tooltip?.borderColor || '#000',
      borderWidth: options?.plugins?.tooltip?.borderWidth || 1,
      textStyle: {
        color: options?.plugins?.tooltip?.bodyColor || '#000',
      },
      formatter: buildTooltipFormatter(options, updatedSeries, dataSets),
    },
    legend: {
      show: options?.plugins?.legend?.display !== false,
      bottom: 0,
      textStyle: {
        color: options?.plugins?.legend?.labels?.color || '#000',
      },
    },
    grid: {
      left: '3%',
      right: hasDualAxis ? '10%' : '3%',
      bottom: '15%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: actualXLabels,
      boundaryGap: type === 'bar',
      axisLabel: {
        rotate: options?.scales?.x?.ticks?.maxRotation || 45,
        color: options?.scales?.x?.ticks?.color || '#000',
        fontSize: 10,
        interval: 'auto',
        showMinLabel: true,
        showMaxLabel: true,
        hideOverlap: true,
      },
      axisLine: {
        lineStyle: {
          color: options?.scales?.x?.grid?.color || '#ccc',
        },
      },
    },
    yAxis: yAxisConfig,
    series: updatedSeries,
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        zoomLock: false,
      },
      {
        type: 'slider',
        start: 0,
        end: 100,
        height: 20,
        bottom: 30,
        handleSize: '80%',
        showDetail: false,
      },
    ],
  };
};

/**
 * Builds weather forecast chart configuration with weather icons and wind direction arrows.
 * Automatically adds mark points for visual weather indicators.
 *
 * Used when options.plugins.weatherIconAtPoint or options.plugins.windDirectionIcon is enabled.
 *
 * @param {Object} params - Configuration parameters
 * @param {Array} params.xAxisData - X-axis data labels
 * @param {Array} params.series - ECharts series array
 * @param {Object} params.dataSets - Chart.js datasets object
 * @param {Object} params.options - Chart.js options with weather plugin config:
 *   - options.plugins.weatherIconAtPoint.enabled - Enable weather icons
 *   - options.plugins.weatherIconAtPoint.datasets - Array of dataset indices to apply icons to
 *   - options.plugins.weatherIconAtPoint.iconIds - Array of icon IDs
 *   - options.plugins.windDirectionIcon.enabled - Enable wind arrows
 *   - options.plugins.windDirectionIcon.datasets - Array of dataset indices for arrows
 * @param {string} params.type - Chart type ('line', 'bar', etc.)
 * @returns {Object} ECharts configuration with weather mark points
 */
export const buildWeatherChartConfig = ({ xAxisData, series, dataSets, options, type }) => {
  // Start with standard config
  const baseConfig = buildStandardChartConfig({
    xAxisData,
    series,
    dataSets,
    options,
    type,
  });

  // Update series with yAxisIndex first
  const { yAxisMap } = buildYAxisConfig(dataSets, options);
  const updatedSeries = updateSeriesWithYAxis(series, dataSets, yAxisMap);

  // Add mark points for weather icons and wind direction
  const seriesWithMarkPoints = createMarkPoints(updatedSeries, options);

  return {
    ...baseConfig,
    series: seriesWithMarkPoints,
    tooltip: {
      ...baseConfig.tooltip,
      formatter: buildTooltipFormatter(options, seriesWithMarkPoints, dataSets),
    },
  };
};

/**
 * Builds Aware device chart configuration with threshold visualization.
 * Adds colored mark areas to highlight threshold events and vertical threshold lines at bottom.
 * Enhanced tooltip follows mouse cursor for better UX.
 *
 * Used when options.plugins.verticalLinePlugin is enabled.
 *
 * @param {Object} params - Configuration parameters
 * @param {Array} params.xAxisData - X-axis data labels
 * @param {Array} params.series - ECharts series array
 * @param {Object} params.dataSets - Chart.js datasets object
 * @param {Object} params.options - Chart.js options with threshold plugin config:
 *   - options.plugins.verticalLinePlugin.enabled - Enable threshold areas
 *   - options.plugins.verticalLinePlugin.events - Array of threshold events
 * @param {string} params.type - Chart type ('line', 'bar', etc.)
 * @returns {Object} ECharts configuration with threshold mark areas and vertical lines
 */
export const buildStageDeviceConfig = ({ xAxisData, series, dataSets, options, type }) => {
  // Start with standard config
  const baseConfig = buildStandardChartConfig({
    xAxisData,
    series,
    dataSets,
    options,
    type,
  });

  // Update series with yAxisIndex first
  const { yAxisMap } = buildYAxisConfig(dataSets, options);
  const updatedSeries = updateSeriesWithYAxis(series, dataSets, yAxisMap);

  // Add mark areas for threshold events
  const seriesWithThresholds = createThresholdMarkAreas(updatedSeries, options);
  // Add vertical threshold indicator lines at bottom of chart using markLine
  const thresholdColors = ['#ffCC00', '#ff7b00ff', '#ff0000']; // Yellow, Orange, Red

  // Collect all threshold events across all series
  const allThresholdEvents = [];
  seriesWithThresholds.forEach((seriesItem, seriesIdx) => {
    const rawData = seriesItem._rawData; // Has x,y format
    const metaData = seriesItem._meta?.data; // Has redLine and other metadata

    if (!rawData || rawData.length === 0 || !metaData || metaData.length === 0) {
      return;
    }

    // Iterate through rawData and get redLine from corresponding metaData
    rawData.forEach((point, index) => {
      const metaPoint = metaData[index]; // Get corresponding metadata
      const thresholdLevel = metaPoint?.redLine || 0;

      if (thresholdLevel > 0 && thresholdLevel <= 3) {
        // Round timestamp to match X-axis format (remove seconds)
        const timestampWithSeconds = point.x;
        const timestampRounded = new Date(timestampWithSeconds);
        timestampRounded.setSeconds(0, 0);
        const xValueRounded = timestampRounded.toLocaleString();

        allThresholdEvents.push({
          xValue: xValueRounded, // Use rounded timestamp to match X-axis categories
          index: index,
          level: thresholdLevel,
          color: thresholdColors[thresholdLevel - 1],
        });
      }
    });
  });

  // TEMPORARILY: Add lines at ALL data points to test visibility - REMOVE WHEN DONE TESTING
  const testThresholdEvents = [];
  // Comment out test events once real threshold detection is working
  /*
  if (seriesWithThresholds.length > 0 && seriesWithThresholds[0]._rawData) {
    // Add test lines spread across the data range
    const rawData = seriesWithThresholds[0]._rawData;
    const step = Math.max(1, Math.floor(rawData.length / 10)); // Spread 10 lines across entire dataset
    for (let i = 0; i < rawData.length; i += step) {
      testThresholdEvents.push({
        xValue: rawData[i].x, // Use actual X-axis label
        index: i,
        level: ((i / step) % 3) + 1,
        color: thresholdColors[Math.floor(i / step) % 3],
      });
    }
  }
  */

  // Merge test events with actual threshold events
  const eventsToRender = [...allThresholdEvents, ...testThresholdEvents];

  if (eventsToRender.length > 0 && seriesWithThresholds.length > 0) {
    // Get Y-axis configuration to calculate actual values
    const yAxisConfig = Array.isArray(baseConfig.yAxis) ? baseConfig.yAxis[0] : baseConfig.yAxis;

    // Find the actual min/max Y values from the data
    let dataMin = Infinity;
    let dataMax = -Infinity;
    seriesWithThresholds.forEach((s) => {
      if (s.data && s.data.length > 0) {
        s.data.forEach((v) => {
          if (v !== null && v !== undefined) {
            dataMin = Math.min(dataMin, v);
            dataMax = Math.max(dataMax, v);
          }
        });
      }
    });

    // Use configured min or calculated min
    const yMin =
      yAxisConfig.min !== undefined ? yAxisConfig.min : dataMin !== Infinity ? dataMin : 0;
    const yMax =
      yAxisConfig.max !== undefined ? yAxisConfig.max : dataMax !== -Infinity ? dataMax : 100;
    const yRange = yMax - yMin;

    // Line height: 5% of the Y range
    const lineHeight = yRange * 0.08;

    // Use TWO-POINT format: array of [startPoint, endPoint]
    const markLineData = eventsToRender.map((event, idx) => {
      return [
        {
          xAxis: event.xValue || event.index, // Use actual X-axis value (timestamp) not index
          yAxis: yMin, // Start at bottom
          symbol: 'none',
          lineStyle: {
            color: event.color,
            width: 5,
            type: 'solid',
          },
        },
        {
          xAxis: event.xValue || event.index, // Same X position
          yAxis: yMin + lineHeight, // End above bottom
          symbol: 'none',
        },
      ];
    });

    // Add to first series
    seriesWithThresholds[0].markLine = {
      silent: true,
      symbol: 'none',
      data: markLineData,
      animation: false,
    };
  }

  return {
    ...baseConfig,
    series: seriesWithThresholds.map((series, idx) => {
      if (series._meta?.data) {
        // Get the device's base color from the series (ECharts format or original dataset)
        const deviceColor =
          series.itemStyle?.color ||
          series.lineStyle?.color ||
          series.backgroundColor ||
          series.borderColor ||
          'rgb(255, 165, 0)';

        // Create array with color for each data point
        // Only show threshold colors when redLine > 0 (threshold exceeded)
        // Otherwise use device's assigned color
        const lineColors = series._meta.data.map((point) => {
          const thresholdLevel = point?.redLine || 0;

          // Only apply threshold colors if threshold is actually exceeded
          if (thresholdLevel > 0) {
            if (thresholdLevel === 3) return '#ff0000'; // Red
            if (thresholdLevel === 2) return '#ff7b00ff'; // Orange
            if (thresholdLevel === 1) return '#ffCC00'; // Yellow
          }

          // Use device's color when no threshold exceeded
          return deviceColor;
        });

        return {
          ...series,
          // Color individual points - only during threshold events
          itemStyle: {
            ...series.itemStyle,
            color: function (params) {
              const metaData = series._meta.data[params.dataIndex];
              const thresholdLevel = metaData?.redLine || 0;

              // Only color points during threshold events
              if (thresholdLevel > 0) {
                if (thresholdLevel === 3) return '#ff0000'; // Red
                if (thresholdLevel === 2) return '#ff7b00ff'; // Orange
                if (thresholdLevel === 1) return '#ffCC00'; // Yellow
              }

              // Use device's assigned color otherwise
              return deviceColor;
            },
          },
          emphasis: {
            lineStyle: {
              width: 4,
            },
          },
          // Use gradient with device color as base, threshold colors only during events
          lineStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: lineColors.map((color, index) => ({
                offset: index / (lineColors.length - 1 || 1),
                color: color,
              })),
            },
            width: 3,
          },
        };
      }
      return series;
    }),
    tooltip: {
      ...baseConfig.tooltip,
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#999',
        },
      },
      // Make tooltip follow mouse cursor
      position: function (point, params, dom, rect, size) {
        // point is mouse position [x, y]
        // size has chart width/height and tooltip width/height
        let x = point[0];
        let y = point[1];

        // Keep tooltip within chart bounds
        const chartWidth = size.viewSize[0];
        const chartHeight = size.viewSize[1];
        const tooltipWidth = size.contentSize[0];
        const tooltipHeight = size.contentSize[1];

        // Position tooltip to the right and below cursor, but adjust if near edge
        x = Math.min(x + 15, chartWidth - tooltipWidth - 10);
        y = Math.min(y + 15, chartHeight - tooltipHeight - 10);

        // If too close to right edge, show on left side
        if (x + tooltipWidth > chartWidth - 20) {
          x = point[0] - tooltipWidth - 15;
        }

        // If too close to bottom, show above
        if (y + tooltipHeight > chartHeight - 20) {
          y = point[1] - tooltipHeight - 15;
        }

        return [x, y];
      },
      confine: true, // Keep tooltip within chart area
      formatter: buildTooltipFormatter(options, seriesWithThresholds, dataSets),
    },
  };
};

/**
 * Builds custom chart configuration with user-defined modifications.
 * Allows merging custom ECharts config through options.customConfig.
 *
 * @param {Object} params - Configuration parameters
 * @param {Array} params.xAxisData - X-axis data labels
 * @param {Array} params.series - ECharts series array
 * @param {Object} params.dataSets - Chart.js datasets object
 * @param {Object} params.options - Chart.js options object:
 *   - options.customConfig - Custom ECharts config to merge
 * @param {string} params.type - Chart type ('line', 'bar', etc.)
 * @param {string} customType - Custom chart type identifier
 * @returns {Object} ECharts configuration merged with custom config
 */
export const buildCustomChartConfig = (
  { xAxisData, series, dataSets, options, type },
  customType,
) => {
  // Start with standard config as base
  const baseConfig = buildStandardChartConfig({
    xAxisData,
    series,
    dataSets,
    options,
    type,
  });

  // Allow custom modifications through options.customConfig
  const customConfig = options?.customConfig || {};

  return {
    ...baseConfig,
    ...customConfig,
  };
};
